import {Routes} from "@angular/router";
import {CompanyRoutes} from "./x-models/x-enums/company-routes";
import {ShiftMakerComponent} from "./shift-maker/shift-maker.component";
import {AuthGuard} from "../../_auth/auth.guard";
import {PermissionService} from "../../_auth/permission.service";
import {EmploymentRelationshipsComponent} from "./employment-relationships/employment-relationships.component";
import {ContractsComponent} from "./contracts/contracts.component";
import {FormManagerComponent} from "../forms/manager/form-manager.component";
import {BuilderComponent} from "../forms/builder/builder.component";
import {FormRequirementsComponent} from "../forms/requirements/form-requirements.component";
import {unsavedChangesGuard} from "../forms/builder/guards/unsaved-changes.guard";
import {SubmissionListComponent} from "../forms/submissions/submission-list.component";
import {SubmissionDetailComponent} from "../forms/submissions/submission-detail.component";
import {PayrollInvitationsComponent} from "./payroll/payroll-invitations.component";
import {AccessManagementComponent} from "./access/access-management.component";
import {DeviceConfigComponent} from "../device-config/manager/device-config.component";
import {Roles} from "../../_auth/x-enums/roles";
import {ContactRequirementsComponent} from "./employees/contact-requirements/contact-requirements.component";

export const COMPANY_ROUTES: Routes = [
  {
    // Scoped role grants + invitations (#261/#279). The three roles are exactly the controller's
    // `[Authorize]` list; `GrantIssuancePolicy` narrows further per request, and a refusal renders as its own
    // message on the page. PlatformOwner must be listed for the route to be reachable at all by a
    // company-INDEPENDENT platform session, which `AuthGuard` otherwise bounces to the platform landing —
    // and that session is precisely the one that has to pick a company here.
    path: CompanyRoutes.Access,
    component: AccessManagementComponent,
    canActivate: [AuthGuard],
    data: {roles: [Roles.PlatformOwner, Roles.CompanyOwner, Roles.ShardAdmin]},
    title: "Zugänge"
  },
  {
    path: CompanyRoutes.PayrollInvitations,
    component: PayrollInvitationsComponent,
    canActivate: [AuthGuard],
    // Same audience as employment-relationship management (company owner / shard admin) — and literally the
    // same PermissionSet, so `Roles.CompanyOwner` (#279) reaches both. `PayrollInvitationController` has named
    // `PlatformOwner,CompanyOwner,ShardAdmin` since it was written, so nothing here 403s for an owner.
    data: PermissionService.Permissions_EmploymentRelationshipComponent().routeData(),
    title: "Lohnbuchhalter"
  },
  {
    path: CompanyRoutes.ShiftMaker, component: ShiftMakerComponent,
    canActivate: [AuthGuard],
    // `routeData()` rather than a hand-picked `.default`: the navbar entry and this route are then driven by
    // the SAME PermissionSet, so a role added for one is automatically true of the other. It carries both the
    // AND-evaluated legacy ids and the OR-evaluated central roles — and that second half is the whole point:
    // `.default` alone silently dropped `ShiftLead` (#280) and left the route unreachable for the role.
    data: PermissionService.Permissions_ShiftMakerComponent().routeData(),
    title: "Dienstplan"
  },
  {
    // The worktime-stats overview was merged INTO the employees page (#140); keep the old URL working.
    path: CompanyRoutes.WorktimeStats,
    redirectTo: '/company/employee'
  },
  {
    path: CompanyRoutes.EmployeeRelationships,
    component: EmploymentRelationshipsComponent,
    canActivate: [AuthGuard],
    // `payroll: true` lets an external Lohnbuchhalter reach this read-only view (salary/limits); the AuthGuard
    // gates every other company route away from a payroll session. `routeData()` carries the OR-list beside it,
    // which is how a company-only `CompanyOwner` reaches it (#279) — the two are independent doors.
    data: {...PermissionService.Permissions_EmploymentRelationshipComponent().routeData(), payroll: true},
    title: "Beschäftigungsverhältnisse"
  }, {
    path: CompanyRoutes.Contracts,
    component: ContractsComponent,
    canActivate: [AuthGuard],
    data: PermissionService.Permissions_ContractComponent().routeData(),
    title: "Verträge"
  },
  {
    // Required contact-channel linking (#558). Same audience as the scoped-role-grants screen (Access) — the
    // manager endpoints behind it are ShardAdmin/CompanyOwner/PlatformOwner — so it carries that exact role
    // list rather than a PermissionSet: this feature has no legacy endpoint-permission id to key one on.
    path: CompanyRoutes.ContactRequirements,
    component: ContactRequirementsComponent,
    canActivate: [AuthGuard],
    data: {roles: [Roles.PlatformOwner, Roles.CompanyOwner, Roles.ShardAdmin]},
    title: "Pflicht-Kontakte"
  },
  {
    path: CompanyRoutes.Forms,
    component: FormManagerComponent,
    canActivate: [AuthGuard],
    title: "Formulare"
  },
  {
    path: CompanyRoutes.FormBuilder,
    component: BuilderComponent,
    canActivate: [AuthGuard],
    canDeactivate: [unsavedChangesGuard],
    title: "Form Builder"
  },
  {
    path: CompanyRoutes.FormRequirements,
    component: FormRequirementsComponent,
    canActivate: [AuthGuard],
    title: "Pflicht-Zuweisung"
  },
  {
    path: CompanyRoutes.SubmissionDetail,
    component: SubmissionDetailComponent,
    canActivate: [AuthGuard],
    // The payroll accountant reviews submissions, so it must reach the detail view (the guard's payroll branch
    // otherwise bounces it to the employee list).
    data: {payroll: true},
    title: "Formulareingabe"
  },
  {
    path: CompanyRoutes.Submissions,
    component: SubmissionListComponent,
    canActivate: [AuthGuard],
    title: "Formulareingaben"
  },
  {
    path: CompanyRoutes.DeviceConfig,
    component: DeviceConfigComponent,
    canActivate: [AuthGuard],
    // Manage the PoS register configuration: shard admins (own shard) + company owners + platform owners.
    data: {roles: [Roles.ShardAdmin, Roles.CompanyOwner, Roles.PlatformOwner]},
    title: "Geräte-Konfiguration"
  }
]
